<template>
  <div class="db-shell">
    <!-- ==================== Header ==================== -->
    <header class="db-header">
      <div>
        <div class="eyebrow">Overview</div>
        <h1>Dashboard</h1>
        <p class="subtitle">Live pay-in, pay-out, and settlement activity across your workspace.</p>
      </div>
    </header>

    <!-- ==================== Support view ==================== -->
    <template v-if="isSupport">
      <section class="db-section">
        <h2 class="section-title">Support tickets</h2>
        <div class="stat-grid stat-grid-4">
          <div class="stat-card tone-neutral">
            <div class="stat-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
                <path d="M14 3H6a2 2 0 00-2 2v14a2 2 0 002 2h12a2 2 0 002-2V9l-6-6z" stroke-linecap="round" stroke-linejoin="round" />
                <path d="M14 3v6h6" stroke-linecap="round" />
              </svg>
            </div>
            <div class="stat-body">
              <div class="stat-label">Total tickets</div>
              <div class="stat-value">{{ getTotalTickets }}</div>
            </div>
          </div>
          <div class="stat-card tone-pending">
            <div class="stat-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
                <circle cx="12" cy="12" r="10" />
                <path d="M12 6v6l4 2" stroke-linecap="round" />
              </svg>
            </div>
            <div class="stat-body">
              <div class="stat-label">Pending</div>
              <div class="stat-value">{{ pendingTicketsCount }}</div>
            </div>
          </div>
          <div class="stat-card tone-success">
            <div class="stat-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
                <path d="M20 6L9 17l-5-5" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
            </div>
            <div class="stat-body">
              <div class="stat-label">Solved</div>
              <div class="stat-value">{{ solvedTicketsCount }}</div>
            </div>
          </div>
          <div class="stat-card tone-danger">
            <div class="stat-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
                <circle cx="12" cy="12" r="10" />
                <path d="M15 9l-6 6M9 9l6 6" stroke-linecap="round" />
              </svg>
            </div>
            <div class="stat-body">
              <div class="stat-label">Not solved</div>
              <div class="stat-value">{{ notSolvedTicketsCount }}</div>
            </div>
          </div>
        </div>
      </section>
    </template>

    <!-- ==================== Order-creator view ==================== -->
    <template v-else-if="isOrderCreator">
      <section v-if="isShowPayInStatistics" class="db-section">
        <h2 class="section-title">Pay-in statistics</h2>
        <div class="hero-row">
          <div class="stat-card tone-success tone-highlight">
            <div class="stat-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M12 2v20M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" stroke-linecap="round" stroke-linejoin="round" /></svg></div>
            <div class="stat-body">
              <div class="stat-label">Total pay-in (approved)</div>
              <div class="stat-value">{{ manualPayInApprovedValue }}</div>
              <div class="stat-sub">Diff: {{ manualPayInApprovedDiff }}</div>
            </div>
          </div>
          <div class="stat-card tone-info">
            <div class="stat-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M12 3v14M6 11l6 6 6-6M4 21h16" stroke-linecap="round" stroke-linejoin="round" /></svg></div>
            <div class="stat-body">
              <div class="stat-label">Total orders</div>
              <div class="stat-value">{{ getManualPayInTotalOrders }}</div>
              <div class="stat-sub">All statuses combined</div>
            </div>
          </div>
        </div>

        <div class="matrix-card">
          <div class="matrix-head">
            <div>
              <div class="matrix-eyebrow">Status breakdown</div>
              <div class="matrix-title">Pay-in orders by status</div>
            </div>
            <div class="matrix-total">
              <span class="matrix-total-count">{{ getManualPayInTotalOrders }}</span>
              <span class="matrix-total-label">orders</span>
            </div>
          </div>
          <div class="matrix-rows matrix-rows-wide">
            <div v-for="s in manualPayInStatusMatrix" :key="s.key" class="matrix-row matrix-row-wide" :class="`row-${s.tone}`">
              <span class="matrix-dot"></span>
              <span class="matrix-label">{{ s.label }}</span>
              <span class="matrix-count">{{ s.count }}</span>
              <span class="matrix-amount">₹{{ s.value || 0 }}</span>
              <div class="matrix-bar"><div class="matrix-bar-fill" :style="{ width: s.percent + '%' }"></div></div>
              <span class="matrix-percent">{{ s.percent }}%</span>
            </div>
          </div>
        </div>
      </section>

      <section v-if="isShowPayOutStatistics" class="db-section">
        <h2 class="section-title">Pay-out statistics</h2>
        <div class="hero-row">
          <div class="stat-card tone-success tone-highlight">
            <div class="stat-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M12 2v20M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" stroke-linecap="round" stroke-linejoin="round" /></svg></div>
            <div class="stat-body">
              <div class="stat-label">Total pay-out (approved)</div>
              <div class="stat-value">{{ manualPayOutApprovedValue }}</div>
            </div>
          </div>
          <div class="stat-card tone-info">
            <div class="stat-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M12 21V7M6 13l6-6 6 6M4 3h16" stroke-linecap="round" stroke-linejoin="round" /></svg></div>
            <div class="stat-body">
              <div class="stat-label">Total orders</div>
              <div class="stat-value">{{ getManualPayOutTotalOrders }}</div>
              <div class="stat-sub">All statuses combined</div>
            </div>
          </div>
        </div>

        <div class="matrix-card">
          <div class="matrix-head">
            <div>
              <div class="matrix-eyebrow">Status breakdown</div>
              <div class="matrix-title">Pay-out orders by status</div>
            </div>
            <div class="matrix-total">
              <span class="matrix-total-count">{{ getManualPayOutTotalOrders }}</span>
              <span class="matrix-total-label">orders</span>
            </div>
          </div>
          <div class="matrix-rows matrix-rows-wide">
            <div v-for="s in manualPayOutStatusMatrix" :key="s.key" class="matrix-row matrix-row-wide" :class="`row-${s.tone}`">
              <span class="matrix-dot"></span>
              <span class="matrix-label">{{ s.label }}</span>
              <span class="matrix-count">{{ s.count }}</span>
              <span class="matrix-amount">₹{{ s.value || 0 }}</span>
              <div class="matrix-bar"><div class="matrix-bar-fill" :style="{ width: s.percent + '%' }"></div></div>
              <span class="matrix-percent">{{ s.percent }}%</span>
            </div>
          </div>
        </div>
      </section>

      <section class="db-filters-section">
        <div class="filters-row">
          <div class="filter-block">
            <label class="filter-label">Window</label>
            <div class="chip-group">
              <button
                v-for="opt in dayOptions"
                :key="opt.value"
                type="button"
                class="chip"
                :class="{ 'chip-active': selectedDay === opt.value }"
                @click="selectedDay = opt.value"
              >{{ opt.label }}</button>
            </div>
          </div>
          <div v-if="isCustomDateSelected" class="filter-block">
            <label class="filter-label">Range</label>
            <div class="date-range">
              <input type="date" id="startDate" v-model.trim="startDate" class="date-input" />
              <span class="range-sep">→</span>
              <input type="date" id="endDate" v-model.trim="endDate" class="date-input" />
            </div>
          </div>
        </div>
      </section>
    </template>

    <!-- ==================== Default view ==================== -->
    <template v-else>
      <section v-if="isShowPayInStatistics" class="db-section">
        <h2 class="section-title">Pay-in statistics</h2>
        <div class="hero-row">
          <div class="stat-card tone-success tone-highlight">
            <div class="stat-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M12 2v20M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" stroke-linecap="round" stroke-linejoin="round" /></svg></div>
            <div class="stat-body">
              <div class="stat-label">Total pay-in</div>
              <div class="stat-value">{{ getTotalPayIn }}</div>
              <div class="stat-sub">Diff: {{ getTotalPayInDiff }}</div>
            </div>
          </div>
          <div class="stat-card tone-info">
            <div class="stat-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M12 3v14M6 11l6 6 6-6M4 21h16" stroke-linecap="round" stroke-linejoin="round" /></svg></div>
            <div class="stat-body">
              <div class="stat-label">Orders</div>
              <div class="stat-value">{{ payInAllCount }}</div>
              <div class="stat-sub">All statuses combined</div>
            </div>
          </div>
        </div>

        <div class="matrix-card">
          <div class="matrix-head">
            <div>
              <div class="matrix-eyebrow">Status breakdown</div>
              <div class="matrix-title">Pay-in orders by status</div>
            </div>
            <div class="matrix-total">
              <span class="matrix-total-count">{{ payInAllCount }}</span>
              <span class="matrix-total-label">orders</span>
            </div>
          </div>
          <div class="matrix-rows">
            <div v-for="s in payInStatusMatrix" :key="s.key" class="matrix-row" :class="`row-${s.tone}`">
              <span class="matrix-dot"></span>
              <span class="matrix-label">{{ s.label }}</span>
              <span class="matrix-count">{{ s.count }}</span>
              <div class="matrix-bar"><div class="matrix-bar-fill" :style="{ width: s.percent + '%' }"></div></div>
              <span class="matrix-percent">{{ s.percent }}%</span>
            </div>
          </div>
        </div>
      </section>

      <section v-if="isShowPayOutStatistics" class="db-section">
        <h2 class="section-title">Pay-out statistics</h2>
        <div class="hero-row">
          <div class="stat-card tone-success tone-highlight">
            <div class="stat-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M12 2v20M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" stroke-linecap="round" stroke-linejoin="round" /></svg></div>
            <div class="stat-body">
              <div class="stat-label">Total pay-out</div>
              <div class="stat-value">{{ getTotalPayOut }}</div>
            </div>
          </div>
          <div class="stat-card tone-info">
            <div class="stat-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M12 21V7M6 13l6-6 6 6M4 3h16" stroke-linecap="round" stroke-linejoin="round" /></svg></div>
            <div class="stat-body">
              <div class="stat-label">Orders</div>
              <div class="stat-value">{{ payOutAllCount }}</div>
              <div class="stat-sub">All statuses combined</div>
            </div>
          </div>
        </div>

        <div class="matrix-card">
          <div class="matrix-head">
            <div>
              <div class="matrix-eyebrow">Status breakdown</div>
              <div class="matrix-title">Pay-out orders by status</div>
            </div>
            <div class="matrix-total">
              <span class="matrix-total-count">{{ payOutAllCount }}</span>
              <span class="matrix-total-label">orders</span>
            </div>
          </div>
          <div class="matrix-rows">
            <div v-for="s in payOutStatusMatrix" :key="s.key" class="matrix-row" :class="`row-${s.tone}`">
              <span class="matrix-dot"></span>
              <span class="matrix-label">{{ s.label }}</span>
              <span class="matrix-count">{{ s.count }}</span>
              <div class="matrix-bar"><div class="matrix-bar-fill" :style="{ width: s.percent + '%' }"></div></div>
              <span class="matrix-percent">{{ s.percent }}%</span>
            </div>
          </div>
        </div>
      </section>

      <!-- <section class="db-section">
        <h2 class="section-title">Total statistics</h2>
        <div class="stat-grid stat-grid-4">
          <div class="stat-card tone-navy tone-highlight">
            <div class="stat-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M3 6h18M3 12h18M3 18h18" stroke-linecap="round" /></svg></div>
            <div class="stat-body">
              <div class="stat-label">Settlement amount</div>
              <div class="stat-value">{{ settlementAmount }}</div>
            </div>
          </div>
          <div class="stat-card tone-purple">
            <div class="stat-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M20 12V8H6a2 2 0 010-4h12v4M20 12H4v8a2 2 0 002 2h14v-8h-4" stroke-linecap="round" stroke-linejoin="round" /><circle cx="16" cy="16" r="1.5" /></svg></div>
            <div class="stat-body">
              <div class="stat-label">Pay-in commission</div>
              <div class="stat-value">{{ payInCommissionAmount }}</div>
            </div>
          </div>
          <div class="stat-card tone-purple">
            <div class="stat-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M20 12V8H6a2 2 0 010-4h12v4M20 12H4v8a2 2 0 002 2h14v-8h-4" stroke-linecap="round" stroke-linejoin="round" /><circle cx="16" cy="16" r="1.5" /></svg></div>
            <div class="stat-body">
              <div class="stat-label">Pay-out commission</div>
              <div class="stat-value">{{ payOutCommissionAmount }}</div>
            </div>
          </div>
          <div class="stat-card tone-coral">
            <div class="stat-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M12 2v20M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" stroke-linecap="round" stroke-linejoin="round" /></svg></div>
            <div class="stat-body">
              <div class="stat-label">Amount to be paid</div>
              <div class="stat-value">{{ amountToBePaid }}</div>
            </div>
          </div>
        </div>
      </section> -->

      <!-- Chart section with all filters -->
      <section class="db-section">
        <div class="chart-card">
          <div class="chart-head">
            <div>
              <div class="chart-eyebrow">Insight</div>
              <div class="chart-title">Hourly {{ paymentType === 'payout' ? 'pay-out' : 'pay-in' }} volume</div>
            </div>
            <div class="chart-filters">
              <div v-if="isSuperAdmin" class="select-wrap select-wrap-sm">
                <select v-model="vendor">
                  <option value="">All vendors</option>
                  <option v-for="(v, index) in vendors" :key="index" :value="v.username">{{ v.username }}</option>
                </select>
                <svg class="select-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M6 9l6 6 6-6" stroke-linecap="round" stroke-linejoin="round" /></svg>
              </div>
              <!-- <div v-if="isAdmin || isSubAdmin" class="select-wrap select-wrap-sm">
                <select v-model="orderCreator">
                  <option value="">All creators</option>
                  <option v-for="(oc, index) in orderCreators" :key="index" :value="oc.username">{{ oc.username }}</option>
                </select>
                <svg class="select-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M6 9l6 6 6-6" stroke-linecap="round" stroke-linejoin="round" /></svg>
              </div> -->
              <div class="select-wrap select-wrap-sm">
                <select v-model="paymentType">
                  <option v-for="(item, index) in paymentTypeList" :key="index" :value="item.value">{{ item.name }}</option>
                </select>
                <svg class="select-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M6 9l6 6 6-6" stroke-linecap="round" stroke-linejoin="round" /></svg>
              </div>
              <div class="chip-group chip-group-sm">
                <button
                  v-for="opt in dayOptions"
                  :key="opt.value"
                  type="button"
                  class="chip chip-sm"
                  :class="{ 'chip-active': selectedDay === opt.value }"
                  @click="selectedDay = opt.value"
                >{{ opt.label }}</button>
              </div>
              <div v-if="isCustomDateSelected" class="date-range">
                <input type="date" id="startDate" v-model.trim="startDate" class="date-input" />
                <span class="range-sep">→</span>
                <input type="date" id="endDate" v-model.trim="endDate" class="date-input" />
              </div>
              <!-- <div class="select-wrap select-wrap-sm">
                <select v-model="website">
                  <option value="">All sites</option>
                  <option v-for="(w, index) in websites" :key="index" :value="w.website">{{ w.website }}</option>
                </select>
                <svg class="select-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M6 9l6 6 6-6" stroke-linecap="round" stroke-linejoin="round" /></svg>
              </div> -->
            </div>
          </div>
          <div class="chart-body">
            <apexchart type="bar" height="350" :options="chartOptions" :series="series"></apexchart>
          </div>
        </div>
      </section>
    </template>
  </div>
</template>

<script>
import http from "../../http-common.js";
export default {
  name: "dashboard",
  data: function () {
    return {
      startDate: "",
      endDate: "",
      isCustomDateSelected: false,
      website: "",
      selectedDay: "today",
      dayOptions: [
        { value: "today",      label: "Today" },
        { value: "yesterday",  label: "Yesterday" },
        { value: "customdate", label: "Custom range" },
      ],
      chartOptions: {
        chart: {
          type: "bar",
          height: 350,
          toolbar: { show: false },
          fontFamily: 'inherit',
        },
        plotOptions: {
          bar: {
            horizontal: false,
            columnWidth: "55%",
            endingShape: "rounded",
            borderRadius: 4,
          },
        },
        dataLabels: {
          enabled: false,
        },
        stroke: {
          show: true,
          width: 2,
          colors: ["transparent"],
        },
        xaxis: {
          categories: Array.from({ length: 24 }, (_, i) => i),
          title: {
            text: "Hour",
            style: { color: '#8a8371', fontWeight: 600, fontSize: '11px' },
          },
          labels: { style: { colors: '#94a3b8', fontSize: '11px' } },
          axisBorder: { color: '#ede9df' },
          axisTicks: { color: '#ede9df' },
        },
        yaxis: {
          title: {
            text: "Amount",
            style: { color: '#8a8371', fontWeight: 600, fontSize: '11px' },
          },
          labels: { style: { colors: '#94a3b8', fontSize: '11px' } },
        },
        grid: { borderColor: '#f1ede2', strokeDashArray: 4 },
        colors: ['#1B2A47'],
        fill: {
          opacity: 1,
          type: 'gradient',
          gradient: {
            shade: 'dark',
            type: 'vertical',
            gradientToColors: ['#2C3E63'],
            stops: [0, 100],
          },
        },
        tooltip: {
          y: {
            formatter: function (val) {
              return "₹ " + val.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, "$&,");
            },
          },
        },
      },
      websites: [],
      paymentType: "",
      orderCreators: [],
      orderCreator: "",
      vendors: [],
      vendor: "",
      pendingTicketsCount: 0,
      solvedTicketsCount: 0,
      notSolvedTicketsCount: 0,
      manualPayInCreatedCount: 0,
      manualPayInCreatedValue: 0,
      manualPayInCreatedDiff: 0,
      manualPayInPendingCount: 0,
      manualPayInPendingValue: 0,
      manualPayInPendingDiff: 0,
      manualPayInApprovedCount: 0,
      manualPayInApprovedValue: 0,
      manualPayInApprovedDiff: 0,
      manualPayInFailedCount: 0,
      manualPayInFailedValue: 0,
      manualPayInFailedDiff: 0,
      manualPayInRejectedCount: 0,
      manualPayInRejectedValue: 0,
      manualPayInRejectedDiff: 0,
      manualPayInExpiredCount: 0,
      manualPayInExpiredValue: 0,
      manualPayInExpiredDiff: 0,
      manualPayInUnassignedCount: 0,
      manualPayInUnassignedValue: 0,
      manualPayInUnassignedDiff: 0,
      manualPayOutCreatedCount: 0,
      manualPayOutCreatedValue: 0,
      manualPayOutPendingCount: 0,
      manualPayOutPendingValue: 0,
      manualPayOutApprovedCount: 0,
      manualPayOutApprovedValue: 0,
      manualPayOutFailedCount: 0,
      manualPayOutFailedValue: 0,
      manualPayOutRejectedCount: 0,
      manualPayOutRejectedValue: 0,
      manualPayOutExpiredCount: 0,
      manualPayOutExpiredValue: 0,
      manualPayOutUnassignedCount: 0,
      manualPayOutUnassignedValue: 0,
      payInCommissionAmount: 0,
      payOutCommissionAmount: 0,
    };
  },
  created() {
    if (this.isSupport) {
      this.getDashboardTicketStatics();
    } else if (this.isOrderCreator) {
      this.getManualOrderStatics();
    } else {
      if (this.isAdmin || this.isSubAdmin) {
        this.getOrderCreatorUsers();
      }
      this.setPaymentType();
      this.getWebsites();
      this.getDashboardOrderStatics();
      this.getOrderTotalStatics();
      this.getBarChartData();
      if (this.isSuperAdmin) {
        this.getVendors();
      }
    }
  },
  watch: {
    selectedDay(neVal) {
      if (neVal == "customdate") {
        this.isCustomDateSelected = true;
      } else {
        this.isCustomDateSelected = false;
        if (this.isSupport) {
          this.getDashboardTicketStatics();
        } else if (this.isOrderCreator) {
          this.getManualOrderStatics();
        } else {
          this.getDashboardOrderStatics();
          this.getOrderTotalStatics();
          this.getBarChartData();
        }
        this.startDate = "";
        this.endDate = "";
      }
    },
    website() {
      this.getDashboardOrderStatics();
      this.getOrderTotalStatics();
      this.getBarChartData();
    },
    startDate(newVal) {
      if (newVal && this.endDate != "") {
        if (this.isSupport) {
          this.getDashboardTicketStatics();
        } else if (this.isOrderCreator) {
          this.getManualOrderStatics();
        } else {
          this.getDashboardOrderStatics();
          this.getOrderTotalStatics();
          this.getBarChartData();
        }
      }
    },
    endDate(newVal) {
      if (newVal && this.startDate != "") {
        if (this.isSupport) {
          this.getDashboardTicketStatics();
        } else if (this.isOrderCreator) {
          this.getManualOrderStatics();
        } else {
          this.getDashboardOrderStatics();
          this.getOrderTotalStatics();
          this.getBarChartData();
        }
      }
    },
    paymentType() {
      this.getBarChartData();
    },
    vendor() {
      this.getDashboardOrderStatics();
      this.getOrderTotalStatics();
      this.getBarChartData();
    },
    orderCreator() {
      this.getDashboardOrderStatics();
      this.getOrderTotalStatics();
      this.getBarChartData();
    },
  },
  computed: {
    getManualPayInTotalOrders() {
      return this.manualPayInCreatedCount + this.manualPayInPendingCount + this.manualPayInApprovedCount + this.manualPayInRejectedCount + this.manualPayInFailedCount + this.manualPayInUnassignedCount + this.manualPayInExpiredCount;
    },
    getManualPayOutTotalOrders() {
      return this.manualPayOutCreatedCount + this.manualPayOutPendingCount + this.manualPayOutApprovedCount + this.manualPayOutRejectedCount + this.manualPayOutFailedCount + this.manualPayOutUnassignedCount + this.manualPayOutExpiredCount;
    },
    isOrderCreator() {
      return this.$store.getters.isOrderCreator;
    },
    isSupport() {
      return this.$store.getters.isSupport;
    },
    isSuperAdmin() {
      return this.$store.getters.isSuperAdmin;
    },
    isAdmin() {
      return this.$store.getters.isAdmin;
    },
    isSubAdmin() {
      return this.$store.getters.isSubAdmin;
    },
    getTotalPayInOperations() {
      return this.$store.getters["payin/totalPayInOperations"];
    },
    getTotalPayIn() {
      return this.$store.getters["payin/totalPayIn"];
    },
    getTotalPayInDiff() {
      return this.$store.getters["payin/totalPayInDiff"];
    },
    getTotalPayOutOperations() {
      return this.$store.getters["payin/totalPayOutOperations"];
    },
    getTotalPayOut() {
      // Match getTotalPayIn's 2-decimal shape. `payin/totalPayOut` comes
      // from mysql2 as a DECIMAL string; parseFloat + toFixed(2) mirrors
      // how getTotalPayIn is presented so the dashboard displays both
      // hero amounts with the same precision.
      const n = parseFloat(this.$store.getters["payin/totalPayOut"]);
      return Number.isFinite(n) ? n.toFixed(2) : "0.00";
    },
    getPayInUnassignedCount() {
      return this.$store.getters["payin/payInUnassignedCount"];
    },
    getPayInFailedCount() {
      return this.$store.getters["payin/payInFailedCount"];
    },
    getPayOutUnassignedCount() {
      return this.$store.getters["payin/payOutUnassignedCount"];
    },
    getPayInPendingCount() {
      return this.$store.getters["payin/payInPendingCount"];
    },
    getPayOutPendingCount() {
      return this.$store.getters["payin/payOutPendingCount"];
    },
    getPayInExpiredCount() {
      return this.$store.getters["payin/payInExpiredCount"];
    },
    getPayInRejectedCount() {
      return this.$store.getters["payin/payInRejectedCount"];
    },
    getPayOutRejectedCount() {
      return this.$store.getters["payin/payOutRejectedCount"];
    },
    getPayOutFailedCount() {
      return this.$store.getters["payin/payOutFailedCount"];
    },
    series() {
      return this.$store.getters["payin/barChartDataSeries"];
    },
    isShowPayInStatistics() {
      if (this.$store.getters.isUser || this.$store.getters.isOrderCreator) {
        if (this.$store.getters.payInStatus == "0") {
          return false;
        } else {
          return true;
        }
      } else {
        return true;
      }
    },
    isShowPayOutStatistics() {
      if (this.$store.getters.isUser || this.$store.getters.isOrderCreator) {
        if (this.$store.getters.payOutStatus == "0") {
          return false;
        } else {
          return true;
        }
      } else {
        return true;
      }
    },
    paymentTypeList() {
      let list = [];
      if (this.isShowPayInStatistics) {
        list.push({
          name: "Pay In",
          value: "payin",
        });
      }
      if (this.isShowPayOutStatistics) {
        list.push({
          name: "Pay Out",
          value: "payout",
        });
      }
      return list;
    },
    getTotalTickets() {
      return this.pendingTicketsCount + this.solvedTicketsCount + this.notSolvedTicketsCount;
    },
    settlementAmount() {
      return (this.getTotalPayIn - this.getTotalPayOut).toFixed(2);
    },
    amountToBePaid() {
      return (this.settlementAmount - this.payInCommissionAmount - this.payOutCommissionAmount).toFixed(2);
    },

    // ==================== Status matrices ====================
    // getTotalPayInOperations already represents the APPROVED payin count.
    // Status matrix rules — matches the payin/payout list quick-filter chips:
    //   Pay-in  : approved, pending, failed, expired  (no unassigned, no rejected)
    //   Pay-out : approved, pending, unassigned, failed  (no expired, no rejected)
    // Rejected was never a real order status in this system — the DB / API
    // uses `failed` for the terminal-not-approved case. Surfacing "Rejected"
    // as a row would always read zero and confuse operators.
    payInApprovedCount() { return Number(this.getTotalPayInOperations) || 0; },
    payInAllCount() {
      return this.payInApprovedCount
        + (Number(this.getPayInPendingCount)  || 0)
        + (Number(this.getPayInFailedCount)   || 0)
        + (Number(this.getPayInExpiredCount)  || 0);
    },
    payInStatusMatrix() {
      const total = this.payInAllCount || 1;
      const rows = [
        { key: 'approved', label: 'Approved', tone: 'success', count: this.payInApprovedCount },
        { key: 'pending',  label: 'Pending',  tone: 'pending', count: Number(this.getPayInPendingCount)  || 0 },
        { key: 'failed',   label: 'Failed',   tone: 'danger',  count: Number(this.getPayInFailedCount)   || 0 },
        { key: 'expired',  label: 'Expired',  tone: 'neutral', count: Number(this.getPayInExpiredCount)  || 0 },
      ];
      return rows.map(r => ({
        ...r,
        percent: this.payInAllCount ? Math.round((r.count / total) * 100) : 0,
      }));
    },

    // Payout equivalent. Payouts have "unassigned" but no "expired".
    payOutApprovedCount() { return Number(this.getTotalPayOutOperations) || 0; },
    payOutAllCount() {
      return this.payOutApprovedCount
        + (Number(this.getPayOutPendingCount)    || 0)
        + (Number(this.getPayOutFailedCount)     || 0)
        + (Number(this.getPayOutUnassignedCount) || 0);
    },
    payOutStatusMatrix() {
      const total = this.payOutAllCount || 1;
      const rows = [
        { key: 'approved',   label: 'Approved',   tone: 'success', count: this.payOutApprovedCount },
        { key: 'pending',    label: 'Pending',    tone: 'pending', count: Number(this.getPayOutPendingCount)    || 0 },
        { key: 'unassigned', label: 'Unassigned', tone: 'info',    count: Number(this.getPayOutUnassignedCount) || 0 },
        { key: 'failed',     label: 'Failed',     tone: 'danger',  count: Number(this.getPayOutFailedCount)     || 0 },
      ];
      return rows.map(r => ({
        ...r,
        percent: this.payOutAllCount ? Math.round((r.count / total) * 100) : 0,
      }));
    },

    // Order-creator view has per-status amounts as well, so the matrix
    // there carries a value column alongside count + percentage.
    manualPayInStatusMatrix() {
      const total = this.getManualPayInTotalOrders || 1;
      const rows = [
        { key: 'approved',   label: 'Approved',   tone: 'success', count: this.manualPayInApprovedCount,   value: this.manualPayInApprovedValue },
        { key: 'pending',    label: 'Pending',    tone: 'pending', count: this.manualPayInPendingCount,    value: this.manualPayInPendingValue },
        { key: 'created',    label: 'Created',    tone: 'neutral', count: this.manualPayInCreatedCount,    value: this.manualPayInCreatedValue },
        { key: 'expired',    label: 'Expired',    tone: 'neutral', count: this.manualPayInExpiredCount,    value: this.manualPayInExpiredValue },
        { key: 'failed',     label: 'Failed',     tone: 'danger',  count: this.manualPayInFailedCount,     value: this.manualPayInFailedValue },
      ];
      return rows.map(r => ({
        ...r,
        percent: this.getManualPayInTotalOrders ? Math.round(((r.count || 0) / total) * 100) : 0,
      }));
    },
    manualPayOutStatusMatrix() {
      const total = this.getManualPayOutTotalOrders || 1;
      const rows = [
        { key: 'approved',   label: 'Approved',   tone: 'success', count: this.manualPayOutApprovedCount,   value: this.manualPayOutApprovedValue },
        { key: 'pending',    label: 'Pending',    tone: 'pending', count: this.manualPayOutPendingCount,    value: this.manualPayOutPendingValue },
        { key: 'created',    label: 'Created',    tone: 'neutral', count: this.manualPayOutCreatedCount,    value: this.manualPayOutCreatedValue },
        { key: 'unassigned', label: 'Unassigned', tone: 'info',    count: this.manualPayOutUnassignedCount, value: this.manualPayOutUnassignedValue },
        { key: 'failed',     label: 'Failed',     tone: 'danger',  count: this.manualPayOutFailedCount,     value: this.manualPayOutFailedValue },
      ];
      return rows.map(r => ({
        ...r,
        percent: this.getManualPayOutTotalOrders ? Math.round(((r.count || 0) / total) * 100) : 0,
      }));
    },
  },
  methods: {
    async getManualOrderStatics() {
      try {
        const params = {
          type: "dashboard",
          selectedDay: this.selectedDay,
          website: this.website,
          startDate: this.startDate,
          endDate: this.endDate,
          vendor: this.vendor,
        };

        const response = await http.get(
          `/order-creator/getManualOrderStatics?selectedDay=${params.selectedDay}&website=${params.website}&startDate=${params.startDate}&endDate=${params.endDate}&vendor=${params.vendor}`
        );

        if (response.status == 200) {
          let manualPayInArrObj = [];
          let manualPayOutArrObj = [];

          const responseData = response.data.data;

          responseData.forEach((obj) => {
            if (obj.type === "payin") {
              const arrObj = {
                paymentStatus: obj.paymentStatus,
                count: obj.count,
                totalAmount: obj.total_amount,
                totalAmountDiff: obj.total_diff_amount,
              };
              manualPayInArrObj.push(arrObj);
            } else if (obj.type === "payout") {
              const arrObj = {
                paymentStatus: obj.paymentStatus,
                count: obj.count,
                totalAmount: obj.total_amount,
              };
              manualPayOutArrObj.push(arrObj);
            }
          });

          let manualPayInArray = {
            unassigned: {
              count: 0,
              value: 0,
            },
            pending: {
              count: 0,
              value: 0,
            },
            approved: {
              count: 0,
              value: 0,
            },
            expired: {
              count: 0,
              value: 0,
            },
            rejected: {
              count: 0,
              value: 0,
            },
            failed: {
              count: 0,
              value: 0,
            },
            created: {
              count: 0,
              value: 0,
            },
          };

          manualPayInArrObj.forEach((element) => {
            manualPayInArray[element.paymentStatus].count = element.count;
            manualPayInArray[element.paymentStatus].value = element.totalAmount;
            manualPayInArray[element.paymentStatus].diff = element.totalAmountDiff;
          });

          this.manualPayInCreatedCount = manualPayInArray.created.count;
          this.manualPayInCreatedValue = manualPayInArray.created.value;
          this.manualPayInCreatedDiff = manualPayInArray.created.diff;
          this.manualPayInPendingCount = manualPayInArray.pending.count;
          this.manualPayInPendingValue = manualPayInArray.pending.value;
          this.manualPayInPendingDiff = manualPayInArray.pending.diff;
          this.manualPayInApprovedCount = manualPayInArray.approved.count;
          this.manualPayInApprovedValue = manualPayInArray.approved.value;
          this.manualPayInApprovedDiff = manualPayInArray.approved.diff;
          this.manualPayInFailedCount = manualPayInArray.failed.count;
          this.manualPayInFailedValue = manualPayInArray.failed.value;
          this.manualPayInFailedDiff = manualPayInArray.failed.diff;
          this.manualPayInRejectedCount = manualPayInArray.rejected.count;
          this.manualPayInRejectedValue = manualPayInArray.rejected.value;
          this.manualPayInRejectedDiff = manualPayInArray.rejected.diff;
          this.manualPayInExpiredCount = manualPayInArray.expired.count;
          this.manualPayInExpiredValue = manualPayInArray.expired.value;
          this.manualPayInExpiredDiff = manualPayInArray.expired.diff;
          this.manualPayInUnassignedCount = manualPayInArray.unassigned.count;
          this.manualPayInUnassignedValue = manualPayInArray.unassigned.value;
          this.manualPayInUnassignedDiff = manualPayInArray.unassigned.diff;


          let manualPayOutArray = {
            unassigned: {
              count: 0,
              value: 0,
            },
            pending: {
              count: 0,
              value: 0,
            },
            approved: {
              count: 0,
              value: 0,
            },
            expired: {
              count: 0,
              value: 0,
            },
            rejected: {
              count: 0,
              value: 0,
            },
            failed: {
              count: 0,
              value: 0,
            },
            created: {
              count: 0,
              value: 0,
            },
          };

          manualPayOutArrObj.forEach((element) => {
            manualPayOutArray[element.paymentStatus].count = element.count;
            manualPayOutArray[element.paymentStatus].value = element.totalAmount;
          });

          this.manualPayOutCreatedCount = manualPayOutArray.created.count;
          this.manualPayOutCreatedValue = manualPayOutArray.created.value;
          this.manualPayOutPendingCount = manualPayOutArray.pending.count;
          this.manualPayOutPendingValue = manualPayOutArray.pending.value;
          this.manualPayOutApprovedCount = manualPayOutArray.approved.count;
          this.manualPayOutApprovedValue = manualPayOutArray.approved.value;
          this.manualPayOutFailedCount = manualPayOutArray.failed.count;
          this.manualPayOutFailedValue = manualPayOutArray.failed.value;
          this.manualPayOutRejectedCount = manualPayOutArray.rejected.count;
          this.manualPayOutRejectedValue = manualPayOutArray.rejected.value;
          this.manualPayOutExpiredCount = manualPayOutArray.expired.count;
          this.manualPayOutExpiredValue = manualPayOutArray.expired.value;
          this.manualPayOutUnassignedCount = manualPayOutArray.unassigned.count;
          this.manualPayOutUnassignedValue = manualPayOutArray.unassigned.value;

        }



      } catch (error) {
        this.error = error.message || "Something failed!";
      }
    },
    async getDashboardOrderStatics() {
      try {
        const payload = {
          type: "dashboard",
          selectedDay: this.selectedDay,
          website: this.website,
          startDate: this.startDate,
          endDate: this.endDate,
          vendor: this.vendor,
          orderCreator: this.orderCreator,
        };
        await this.$store.dispatch("payin/getOrderStatics", payload);
      } catch (error) {
        this.error = error.message || "Something failed!";
      }
    },
    onHoverDate(event) {
      console.log(event);
    },
    onDateClick(event) {
      console.log(event);
    },
    async getBarChartData() {
      try {
        const params = {
          type: "dashboard",
          selectedDay: this.selectedDay,
          website: this.website,
          startDate: this.startDate,
          endDate: this.endDate,
          paymentType: this.paymentType,
          vendor: this.vendor,
          orderCreator: this.orderCreator,
        };
        await this.$store.dispatch("payin/getBarChartData", params);
      } catch (error) {
        this.error = error.message || "Something failed!";
      }
    },
    async getWebsites() {
      try {
        const response = await http.get("/reports/websites");
        if (response.status === 200) {
          this.websites = response.data;
        }
      } catch (error) {
        console.log(error);
      }
    },
    setPaymentType() {
      if (this.isShowPayInStatistics && this.isShowPayOutStatistics) {
        this.paymentType = "payin";
      } else if (this.isShowPayInStatistics) {
        this.paymentType = "payin";
      } else if (this.isShowPayOutStatistics) {
        this.paymentType = "payout";
      } else {
        this.paymentType = "";
      }
    },
    async getOrderCreatorUsers() {
      try {
        const response = await http.get("/users/order_creator");
        if (response.status == 200) {
          this.orderCreators = response.data;
        }
      } catch (error) {
        console.log(error);
      }
    },
    async getVendors() {
      try {
        const response = await http.get("/users/admin");
        if (response.status == 200) {
          this.vendors = response.data;
        }
      } catch (error) {
        console.log(error);
      }
    },
    async getDashboardTicketStatics() {
      try {
        const response = await http.get("/support-user/getDashboardTicketStatics");
        if (response.status === 200) {
          response.data.forEach(item => {
            switch (item.status_description) {
              case 'pending':
                this.pendingTicketsCount = item.record_count;
                break;
              case 'solved':
                this.solvedTicketsCount = item.record_count;
                break;
              case 'not_solved':
                this.notSolvedTicketsCount = item.record_count;
                break;
            }
          });
        }
      } catch (error) {
        console.log(error);
      }
    },
    async getOrderTotalStatics() {
      try {
        const params = {
          type: "dashboard",
          selectedDay: this.selectedDay,
          website: this.website,
          startDate: this.startDate,
          endDate: this.endDate,
          vendor: this.vendor,
          orderCreator: this.orderCreator,
        };
        const response = await http.get(`/orders/getOrderTotalStatics/${params.type}?selectedDay=${params.selectedDay}&website=${params.website}&startDate=${params.startDate}&endDate=${params.endDate}&vendor=${params.vendor}&orderCreator=${params.orderCreator}`);
        if (response.status === 200) {
          const responseData = response.data.data;
          this.payInCommissionAmount = responseData.payInCommissionAmount;
          this.payOutCommissionAmount = responseData.payOutCommissionAmount;
        }
      } catch (error) {
        console.log(error);
      }
    },
  },
};
</script>

<style scoped>
/* ==================== Shell ==================== */
.db-shell {
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: 24px;
  gap: 22px;
  background: linear-gradient(180deg, #fbfaf7 0%, #f4f0e5 100%);
  overflow-y: auto;
}

/* ==================== Header ==================== */
.db-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}
.eyebrow {
  font-size: 11px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #8a8371;
  font-weight: 600;
  margin-bottom: 4px;
}
.db-header h1 {
  font-size: 28px;
  font-weight: 700;
  color: #1b2a47;
  line-height: 1.1;
  margin: 0;
  letter-spacing: -0.01em;
}
.subtitle {
  font-size: 13px;
  color: #64748b;
  margin: 4px 0 0;
  max-width: 640px;
  line-height: 1.5;
}

/* ==================== Sections ==================== */
.db-section {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.section-title {
  font-size: 14px;
  font-weight: 700;
  color: #1b2a47;
  margin: 0;
  letter-spacing: -0.005em;
}

/* ==================== Stat grid ==================== */
.stat-grid {
  display: grid;
  gap: 12px;
}
.stat-grid-4 {
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
}

.stat-card {
  display: flex;
  align-items: center;
  gap: 14px;
  background: #fff;
  border: 1px solid #ede9df;
  border-radius: 14px;
  padding: 14px 16px;
  transition: transform 0.15s, box-shadow 0.15s, border-color 0.15s;
  position: relative;
  overflow: hidden;
}
.stat-card::before {
  content: "";
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 3px;
  background: #cbd5e1;
  border-radius: 14px 0 0 14px;
}
.stat-card:hover {
  transform: translateY(-1px);
  box-shadow: 0 10px 24px -14px rgba(27, 42, 71, 0.14);
  border-color: #d9d3c1;
}

.stat-icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: #f4f0e5;
  color: #64748b;
}
.stat-icon svg { width: 22px; height: 22px; }

.stat-body {
  flex: 1;
  min-width: 0;
}
.stat-label {
  font-size: 11px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #8a8371;
  font-weight: 600;
  line-height: 1.2;
}
.stat-value {
  font-size: 22px;
  font-weight: 800;
  color: #1b2a47;
  letter-spacing: -0.01em;
  margin-top: 4px;
  font-variant-numeric: tabular-nums;
  line-height: 1.1;
  overflow-wrap: anywhere;
}
.stat-sub {
  font-size: 11px;
  color: #94a3b8;
  margin-top: 3px;
  font-variant-numeric: tabular-nums;
}

/* ---------- Tones ---------- */
.tone-info::before    { background: linear-gradient(180deg, #3b82f6, #1e40af); }
.tone-info .stat-icon { background: #dbeafe; color: #1e40af; }

.tone-success::before    { background: linear-gradient(180deg, #10b981, #059669); }
.tone-success .stat-icon { background: #d1fae5; color: #047857; }

.tone-pending::before    { background: linear-gradient(180deg, #f59e0b, #d97706); }
.tone-pending .stat-icon { background: #fef3c7; color: #b45309; }

.tone-danger::before     { background: linear-gradient(180deg, #ef4444, #dc2626); }
.tone-danger .stat-icon  { background: #fee2e2; color: #991b1b; }

.tone-warn::before    { background: linear-gradient(180deg, #f59e0b, #b45309); }
.tone-warn .stat-icon { background: #fef3c7; color: #92400e; }

.tone-neutral::before    { background: linear-gradient(180deg, #94a3b8, #64748b); }
.tone-neutral .stat-icon { background: #f1f5f9; color: #475569; }

.tone-navy::before    { background: linear-gradient(180deg, #1B2A47, #2C3E63); }
.tone-navy .stat-icon { background: #eef2f7; color: #1B2A47; }

.tone-purple::before    { background: linear-gradient(180deg, #a855f7, #7e22ce); }
.tone-purple .stat-icon { background: #f3e8ff; color: #6b21a8; }

.tone-coral::before    { background: linear-gradient(180deg, #D94654, #C73443); }
.tone-coral .stat-icon { background: #fef2f2; color: #b91c1c; }

/* Highlight variant — the "hero" numbers get a subtle gradient bg */
.stat-card.tone-highlight {
  background: linear-gradient(135deg, #fbfaf7 0%, #f4f0e5 100%);
  border-color: #ede9df;
}
.tone-highlight.tone-navy {
  background: linear-gradient(135deg, #1B2A47 0%, #2C3E63 100%);
  border-color: transparent;
  color: #fff;
}
.tone-highlight.tone-navy .stat-label { color: rgba(255, 255, 255, 0.65); }
.tone-highlight.tone-navy .stat-value { color: #fff; }
.tone-highlight.tone-navy .stat-icon  { background: rgba(255, 255, 255, 0.15); color: #fff; }
.tone-highlight.tone-navy::before { display: none; }

/* ==================== Filter row (order-creator view) ==================== */
.db-filters-section {
  display: flex;
  flex-direction: column;
  gap: 12px;
  background: #fff;
  border: 1px solid #ede9df;
  border-radius: 14px;
  padding: 14px;
}
.filters-row {
  display: flex;
  flex-wrap: wrap;
  gap: 18px;
  align-items: center;
}
.filter-block {
  display: flex;
  align-items: center;
  gap: 8px;
}
.filter-label {
  font-size: 10px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #8a8371;
  font-weight: 700;
}

/* ---------- Chips ---------- */
.chip-group { display: flex; gap: 4px; flex-wrap: wrap; }
.chip-group-sm .chip { padding: 6px 10px; font-size: 11.5px; }
.chip {
  border: 1px solid #e2e8f0;
  background: #fff;
  padding: 7px 14px;
  border-radius: 8px;
  font-size: 12px;
  color: #475569;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.12s;
  font-family: inherit;
}
.chip:not(.chip-active):hover { border-color: #cbd5e1; background: #fbfaf7; }
.chip-active {
  background: #eef2f7;
  border-color: #cbd5e1;
  color: #1B2A47;
  font-weight: 600;
}

/* ---------- Select ---------- */
.select-wrap { position: relative; display: inline-flex; align-items: center; }
.select-wrap select {
  appearance: none;
  -webkit-appearance: none;
  padding: 8px 32px 8px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  font-size: 13px;
  background: #fbfaf7;
  color: #0f172a;
  font-family: inherit;
  outline: none;
  cursor: pointer;
  min-width: 140px;
  transition: border-color 0.15s, background 0.15s;
}
.select-wrap-sm select {
  padding: 6px 28px 6px 10px;
  font-size: 12px;
  min-width: 120px;
}
.select-wrap select:focus {
  border-color: #1B2A47;
  background: #fff;
  box-shadow: 0 0 0 3px rgba(27, 42, 71, 0.08);
}
.select-chevron {
  position: absolute;
  right: 10px;
  width: 12px; height: 12px;
  color: #94a3b8;
  pointer-events: none;
}

/* ---------- Date range ---------- */
.date-range {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}
.range-sep { color: #cbd5e1; font-size: 13px; }
.date-input {
  padding: 6px 10px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  font-size: 12px;
  background: #fbfaf7;
  color: #0f172a;
  font-family: inherit;
  outline: none;
  transition: border-color 0.15s, background 0.15s;
}
.date-input:focus {
  border-color: #1B2A47;
  background: #fff;
  box-shadow: 0 0 0 3px rgba(27, 42, 71, 0.08);
}

/* ==================== Hero row (2-up KPI) ==================== */
.hero-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 12px;
  margin-bottom: 12px;
}

/* ==================== Status matrix ==================== */
.matrix-card {
  background: #fff;
  border: 1px solid #ede9df;
  border-radius: 14px;
  padding: 16px 18px;
}
.matrix-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding-bottom: 12px;
  border-bottom: 1px solid #f4f0e5;
  margin-bottom: 14px;
}
.matrix-eyebrow {
  font-size: 10px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #8a8371;
  font-weight: 700;
}
.matrix-title {
  font-size: 14px;
  font-weight: 700;
  color: #1b2a47;
  margin-top: 2px;
  letter-spacing: -0.005em;
}
.matrix-total {
  display: inline-flex;
  align-items: baseline;
  gap: 6px;
}
.matrix-total-count {
  font-size: 18px;
  font-weight: 800;
  color: #1b2a47;
  font-variant-numeric: tabular-nums;
}
.matrix-total-label {
  font-size: 11px;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  font-weight: 600;
}

.matrix-rows {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

/* 5-col grid: dot · label · count · bar · % */
.matrix-row {
  display: grid;
  grid-template-columns: 12px 110px 60px 1fr 44px;
  align-items: center;
  gap: 12px;
}
/* Wide variant for order-creator view — adds an amount column */
.matrix-row-wide {
  grid-template-columns: 12px 110px 60px 110px 1fr 44px;
}

.matrix-dot {
  width: 10px;
  height: 10px;
  border-radius: 999px;
  background: #cbd5e1;
}
.row-success .matrix-dot  { background: #10b981; box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.18); }
.row-pending .matrix-dot  { background: #f59e0b; box-shadow: 0 0 0 3px rgba(245, 158, 11, 0.18); }
.row-warn    .matrix-dot  { background: #b45309; box-shadow: 0 0 0 3px rgba(180, 83, 9, 0.18); }
.row-danger  .matrix-dot  { background: #ef4444; box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.18); }
.row-info    .matrix-dot  { background: #3b82f6; box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.18); }
.row-neutral .matrix-dot  { background: #94a3b8; box-shadow: 0 0 0 3px rgba(148, 163, 184, 0.18); }

.matrix-label {
  font-size: 12.5px;
  color: #334155;
  font-weight: 500;
}
.matrix-count {
  font-size: 13px;
  color: #1b2a47;
  font-weight: 700;
  text-align: right;
  font-variant-numeric: tabular-nums;
}
.matrix-amount {
  font-size: 12px;
  color: #475569;
  font-weight: 600;
  text-align: right;
  font-variant-numeric: tabular-nums;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.matrix-bar {
  height: 6px;
  background: #f1ede2;
  border-radius: 999px;
  overflow: hidden;
}
.matrix-bar-fill {
  height: 100%;
  border-radius: 999px;
  background: #94a3b8;
  transition: width 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}
.row-success .matrix-bar-fill { background: linear-gradient(90deg, #10b981, #059669); }
.row-pending .matrix-bar-fill { background: linear-gradient(90deg, #f59e0b, #d97706); }
.row-warn    .matrix-bar-fill { background: linear-gradient(90deg, #f59e0b, #b45309); }
.row-danger  .matrix-bar-fill { background: linear-gradient(90deg, #ef4444, #dc2626); }
.row-info    .matrix-bar-fill { background: linear-gradient(90deg, #3b82f6, #1e40af); }
.row-neutral .matrix-bar-fill { background: linear-gradient(90deg, #94a3b8, #64748b); }

.matrix-percent {
  font-size: 12px;
  color: #64748b;
  font-weight: 600;
  text-align: right;
  font-variant-numeric: tabular-nums;
}

@media (max-width: 720px) {
  .matrix-row {
    grid-template-columns: 12px 1fr 60px 44px;
  }
  .matrix-row .matrix-bar { display: none; }
  .matrix-row-wide {
    grid-template-columns: 12px 1fr 60px 44px;
  }
  .matrix-row-wide .matrix-amount,
  .matrix-row-wide .matrix-bar { display: none; }
}

/* ==================== Chart ==================== */
.chart-card {
  background: #fff;
  border: 1px solid #ede9df;
  border-radius: 16px;
  padding: 18px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.chart-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  padding-bottom: 12px;
  border-bottom: 1px solid #f4f0e5;
}
.chart-eyebrow {
  font-size: 10px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #8a8371;
  font-weight: 700;
}
.chart-title {
  font-size: 16px;
  font-weight: 700;
  color: #1b2a47;
  margin-top: 2px;
  letter-spacing: -0.01em;
}
.chart-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  justify-content: flex-end;
}
.chart-body {
  min-height: 350px;
}
</style>
